import { getSiteUrl } from '@/lib/locales'
import { toAbsoluteUrl } from '@/lib/schema/utils'

export const OG_IMAGE_WIDTH = 1200
export const OG_IMAGE_HEIGHT = 630
export const OG_IMAGE_FALLBACK_PATH = '/images/og-fallback.png'

const MIN_WIDTH = 1200
const MIN_HEIGHT = 630

const VARIANT_URL_KEYS = ['x2', 'x1', 'webp_x2', 'webp_x1', 'url', 'src', 'original', 'path']

function isUsableUrl(value) {
  if (typeof value !== 'string') {
    return false
  }

  const url = value.trim()
  if (!url || url.startsWith('data:')) {
    return false
  }

  if (/\.svg(\?|#|$)/i.test(url)) {
    return false
  }

  return true
}

function sizeFromUrl(url) {
  if (typeof url !== 'string') {
    return { width: 0, height: 0 }
  }

  const match = url.match(/(?:^|[^\d])(\d{3,5})x(\d{3,5})(?:[^\d]|$)/)
  if (!match) {
    return { width: 0, height: 0 }
  }

  return { width: Number(match[1]), height: Number(match[2]) }
}

function readSize(node) {
  if (typeof node === 'string') {
    return sizeFromUrl(node)
  }

  if (!node || typeof node !== 'object') {
    return { width: 0, height: 0 }
  }

  const fromFields = {
    width: Number(node.width || node.w || node.original_width || 0),
    height: Number(node.height || node.h || node.original_height || 0),
  }

  if (fromFields.width && fromFields.height) {
    return fromFields
  }

  return sizeFromUrl(pickUrlFromVariant(node))
}

function meetsOgSize(...nodes) {
  const sizes = nodes.map(readSize).filter(({ width, height }) => width > 0 && height > 0)
  if (!sizes.length) {
    return true
  }

  return sizes.some(({ width, height }) => width >= MIN_WIDTH && height >= MIN_HEIGHT)
}

function pickUrlFromVariant(variant) {
  if (!variant) {
    return null
  }

  if (typeof variant === 'string') {
    return isUsableUrl(variant) ? variant.trim() : null
  }

  if (typeof variant !== 'object') {
    return null
  }

  for (const key of VARIANT_URL_KEYS) {
    const value = variant[key]
    if (isUsableUrl(value)) {
      return value.trim()
    }
  }

  return null
}

function firstImgFromHtml(html) {
  if (typeof html !== 'string' || !html.includes('<img')) {
    return null
  }

  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i)
  const src = match?.[1]
  return isUsableUrl(src) ? src.trim() : null
}

export function pickSignificantImageUrl(node) {
  if (!node) {
    return null
  }

  if (typeof node === 'string') {
    if (node.includes('<img')) {
      return firstImgFromHtml(node)
    }
    return isUsableUrl(node) ? node.trim() : null
  }

  if (Array.isArray(node)) {
    for (const item of node) {
      const url = pickSignificantImageUrl(item)
      if (url) {
        return url
      }
    }
    return null
  }

  if (typeof node !== 'object') {
    return null
  }

  const variants = [
    node.desktop,
    node.main_page_desktop,
    node.image,
    node.preview,
  ]

  for (const variant of variants) {
    const url = pickUrlFromVariant(variant)
    if (url && meetsOgSize(variant, node)) {
      return url
    }
  }

  const selfUrl = pickUrlFromVariant(node)
  if (selfUrl && meetsOgSize(node)) {
    return selfUrl
  }

  return null
}

function pickFromList(list) {
  if (!list) {
    return null
  }

  if (!Array.isArray(list)) {
    return pickSignificantImageUrl(list.image || list)
  }

  for (const item of list) {
    const url = pickSignificantImageUrl(item?.image || item)
    if (url) {
      return url
    }
  }

  return null
}

function pickFromGallery(images) {
  if (!images) {
    return null
  }

  if (Array.isArray(images)) {
    return pickFromList(images)
  }

  return pickFromList(images.main) || pickFromList(images.modal) || pickSignificantImageUrl(images)
}

function pickFromBlocks(blocks) {
  if (!Array.isArray(blocks)) {
    return null
  }

  for (const block of blocks) {
    const data = block?.data || {}
    const url =
      pickSignificantImageUrl(data.image) ||
      pickFromGallery(data.images) ||
      firstImgFromHtml(data.content)

    if (url) {
      return url
    }
  }

  return null
}

export function findFirstSignificantImage(pageProps = {}) {
  const roots = [pageProps, pageProps.data].filter((root) => root && typeof root === 'object')

  for (const root of roots) {
    const url =
      pickFromList(root.banners) ||
      pickSignificantImageUrl(root.banner) ||
      pickSignificantImageUrl(root.image)

    if (url) {
      return url
    }
  }

  for (const root of roots) {
    const url = pickFromBlocks(root.blocks)
    if (url) {
      return url
    }
  }

  for (const root of roots) {
    const url =
      pickFromList(root.galleries) ||
      pickFromGallery(root.gallery) ||
      firstImgFromHtml(root.content)

    if (url) {
      return url
    }
  }

  return null
}

export function getOgImageUrl(pageProps, host) {
  const siteUrl = getSiteUrl(host)
  const image = findFirstSignificantImage(pageProps) || OG_IMAGE_FALLBACK_PATH
  return toAbsoluteUrl(image, siteUrl)
}
